'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
	afterPopstateListeners,
	attachPageLocationListeners,
	onPageAccepted,
	syncPageLocationContext,
	trackPageView
} from '@/utils/analytics';
import { useAppContext } from './AppContext';

interface RouteAnalyticsProps {
	/** Gates tracking until client config has resolved; flipping true fires the initial page_view. */
	enabled: boolean;
}

/**
 * Fires the host's GA4 page_view on initial load and on each App Router navigation. gtag's
 * auto-pageview is off and `router.push` fires none, so without this in-app navigations go
 * uncounted. Renders nothing.
 *
 * The initial emit waits for BOTH `enabled` (client config resolved) and `authSettled` (auth MFE
 * initialized) so the session-entry page_view — the highest-value event — isn't stamped with a
 * guessed `user_auth_state` before auth resolves for a returning logged-in user.
 */
export const RouteAnalytics = ({ enabled }: RouteAnalyticsProps): null => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { authSettled } = useAppContext();
	const reportedPath = useRef<string | null>(null);
	const current = useRef({ pathname, ready: false });

	useEffect(() => {
		current.current = { pathname, ready: enabled && authSettled };
	});

	const reportPage = useCallback(() => {
		const { pathname: path, ready } = current.current;
		if (!ready || window.rvlifeIgnorePopstate || reportedPath.current === path) return;
		// A later Back or Forward has already moved on; its own render reports that page, even
		// when it returns to the page reported last.
		if (window.location.pathname !== path) {
			reportedPath.current = null;
			return;
		}
		if (trackPageView(path)) reportedPath.current = path;
	}, []);

	useEffect(() => attachPageLocationListeners(), []);

	useEffect(
		() =>
			onPageAccepted(() => {
				syncPageLocationContext();
				reportPage();
			}),
		[reportPage]
	);

	useEffect(() => {
		afterPopstateListeners(syncPageLocationContext);
	}, [pathname, searchParams]);

	// Next.js re-renders before Settings can hold a Back, so wait for every popstate listener.
	useEffect(() => {
		if (!enabled || !authSettled) return;
		afterPopstateListeners(reportPage);
	}, [pathname, enabled, authSettled, reportPage]);

	return null;
};
